import { getConfig } from 'helpers/config';
import { logRequestError } from 'services/analytics/log-request-error';
import { getAccessToken, getRegion } from 'services/auth/auth-storage-manager';

import { createApiClient } from '../../http/client';
import type { ConfigurationApiErrorResponse } from '../types';

import type {
  CreateGreetingRequest,
  CreateGreetingResponse,
  GetGreetingResponse,
  ListGreetingsResponse,
  UpdateGreetingRequest,
} from './types';

/**
 * Greetings are only available in Configuration API v3.6 (v3.5 returns 422 for list_greetings).
 * Use a dedicated client with v3.6 prefix so the rest of the app can keep using v3.5.
 */
const greetingsClientInstance = createApiClient({
  baseURL: getConfig().api,
  getCredentials: getAccessToken,
  getRegion,
  prefix: 'v3.6/configuration/action/',
  onError: logRequestError,
});

/**
 * Configuration API greetings client (v3.6).
 * @see {@link https://platform.text.com/docs/management/configuration-api#greetings}
 */
export const greetingsClient = {
  /**
   * Lists greetings (single page).
   * @see {@link https://platform.text.com/docs/management/configuration-api#list-greetings}
   */
  list: async (pageId?: string) => {
    return greetingsClientInstance.post<ListGreetingsResponse, ConfigurationApiErrorResponse>('list_greetings', {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- v3.6 wire format
      json: pageId !== undefined ? { page_id: pageId } : {},
    });
  },

  /**
   * Gets a greeting by ID.
   * @see {@link https://platform.text.com/docs/management/configuration-api#get-greeting}
   */
  get: async (id: number) => {
    return greetingsClientInstance.post<GetGreetingResponse, ConfigurationApiErrorResponse>('get_greeting', {
      json: { id },
    });
  },

  /**
   * Creates a new greeting.
   * @see {@link https://platform.text.com/docs/management/configuration-api#create-greeting}
   */
  create: async (data: CreateGreetingRequest) => {
    return greetingsClientInstance.post<CreateGreetingResponse, ConfigurationApiErrorResponse>('create_greeting', {
      json: data,
    });
  },

  /**
   * Updates a greeting.
   * @see {@link https://platform.text.com/docs/management/configuration-api#update-greeting}
   */
  update: async (id: number, data: UpdateGreetingRequest) => {
    return greetingsClientInstance.post<void, ConfigurationApiErrorResponse>('update_greeting', {
      json: { id, ...data },
    });
  },

  /**
   * Deletes a greeting.
   * @see {@link https://platform.text.com/docs/management/configuration-api#delete-greeting}
   */
  delete: async (id: number) => {
    return greetingsClientInstance.post<void, ConfigurationApiErrorResponse>('delete_greeting', {
      json: { id },
    });
  },
};
